// Single source of truth for every piece of résumé content on the site.
// Consolidated from all of Akil's résumés (AI Automation Engineer, AI Engineer,
// Oyelabs, Uplers, AppSynergies AI/ML, FlowCV). Where they disagreed, the most
// recent version wins — e.g. CGPA 8.0 and the Jan–Jun 2025 Sashainfinity dates.
// Nothing is duplicated across sections: each skill appears in exactly one
// group, and each project/role appears once.

export const profile = {
  name: "Akil Sadik M H",
  role: "AI Automation Engineer",
  taglines: [
    "LLM Integrations (Claude / OpenAI)",
    "RAG Pipelines",
    "n8n Automation",
    "Full-Stack (React / Node.js)",
  ],
  typewriter: [
    "I ship AI automations.",
    "I integrate Claude & OpenAI.",
    "I build RAG pipelines.",
    "I wire n8n to real systems.",
  ],
  location: "Erode, Tamil Nadu, India",
  phone: "+91 78679 77905",
  phoneHref: "tel:+917867977905",
  email: "akilsadikmh@gmail.com",
  github: "AKILSADIK",
  githubUrl: "https://github.com/AKILSADIK",
  linkedin: "akilsadik",
  linkedinUrl: "https://www.linkedin.com/in/akilsadik/",
  summary:
    "AI Automation Engineer with hands-on production experience integrating LLMs (Claude, OpenAI), building RAG pipelines, and shipping n8n and custom-coded automations end to end. Full-stack builder (React, Node.js, PostgreSQL, Docker) who has connected AI into real client-facing systems — WhatsApp Business automation, ERP, and enterprise dashboards — using Claude Code, MCP, OAuth, and Google Workspace APIs.",
  workingStyle:
    "Comfortable working AI-native and independently, from backend and integration through testing, documentation, and support. I design for error handling, data orchestration and scalable architecture, and document what I build publicly on LinkedIn.",
  strengths: [
    "AI-Native Development",
    "End-to-End Delivery",
    "Independent Ownership",
    "Integration & Debugging",
  ],
  softSkills: [
    "Public Speaking",
    "Leadership",
    "Problem Solving",
    "Adaptability",
    "Decision Making",
    "Time Management",
  ],
  interests: ["AI Automation", "Generative AI", "Content Creation"],
  languages: [
    { name: "Tamil", level: "Native" },
    { name: "English", level: "Fluent" },
  ],
};

export const stats = [
  { value: "6+", label: "Projects Shipped" },
  { value: "4", label: "Roles & Internships" },
  { value: "4", label: "Awards Won" },
  { value: "₹1.5L+", label: "Grants & Prizes" },
];

// Grouped so that no skill appears in more than one group.
export const skillGroups = [
  {
    id: "ai-llm",
    title: "AI / LLM",
    skills: [
      "Claude API",
      "OpenAI API",
      "Gemini",
      "LLaMA",
      "RAG",
      "MCP",
      "Prompt Engineering",
      "Generative AI",
    ],
  },
  {
    id: "automation",
    title: "Automation",
    skills: [
      "n8n",
      "WhatsApp Business API",
      "Webhooks",
      "OAuth",
      "Google Sheets API",
      "Google Calendar API",
      "Telegram API",
      "Airtable",
    ],
  },
  {
    id: "full-stack",
    title: "Full-Stack",
    skills: [
      "React",
      "Node.js",
      "Express",
      "REST APIs",
      "PostgreSQL",
      "HTML/CSS",
      "Flask",
      "WordPress",
    ],
  },
  {
    id: "ml-data",
    title: "ML & Data",
    skills: [
      "TensorFlow",
      "PyTorch",
      "Fine-Tuning (QLoRA)",
      "ChromaDB",
      "Data Analytics",
      "Tableau",
    ],
  },
  {
    id: "languages",
    title: "Languages",
    skills: ["Python", "JavaScript", "SQL", "Java"],
  },
  {
    id: "tools",
    title: "Tools / DevOps",
    skills: [
      "Claude Code",
      "Cursor",
      "Docker",
      "Git",
      "GitHub",
      "Postman",
      "Linux",
      "Android Studio",
      "Firebase",
      "Meta",
      "AWS",
      "GCP",
    ],
  },
];

export const experience = [
  {
    id: "forgemind",
    role: "AI Trainer Lead",
    altRole: "AI Automation Engineer",
    company: "Forgemind AI",
    period: "May 2026 – Jul 2026",
    current: true,
    points: [
      "Built and shipped production AI-integrated applications end to end (React, Node.js, PostgreSQL, Docker), working AI-native with Claude Code as the primary development tool.",
      "Developed a WhatsApp Business API management platform: multi-number inbox, bulk messaging, template builder, and sales pipeline (CRM), combining n8n automation with custom Node.js workflows and Claude MCP integrations.",
      "Integrated Claude and OpenAI LLM APIs, MCP servers, Google Workspace APIs (Sheets, Calendar), OAuth authentication, and REST APIs across client projects, and built RAG pipelines to ground AI assistants in each client's data.",
      "Built UWPS, an employee performance-management platform (task tracking, chat, performance analytics, and appraisal workflows) for an international partner, aimed at fair, data-driven promotions.",
      "Built an ERP system for an import/export business, connecting factory, client, and middleman workflows into one streamlined, fully-tracked platform.",
    ],
    stack: [
      "Claude API",
      "OpenAI API",
      "MCP",
      "RAG",
      "n8n",
      "React",
      "Node.js",
      "PostgreSQL",
      "Docker",
    ],
  },
  {
    id: "sashainfinity",
    role: "Web Development Intern",
    company: "Sashainfinity",
    period: "Jan 2025 – Jun 2025",
    points: [
      "Developed and launched a landing page and a Moodle LMS; gained hands-on experience with WordPress, domain management, and shared hosting.",
    ],
    stack: ["WordPress", "HTML/CSS"],
  },
  {
    id: "oasis-infobyte",
    role: "Data Science Intern",
    company: "Oasis Infobyte",
    period: "Jul 2023 – Aug 2023",
    points: [
      "Built predictive models for car sales forecasting, iris classification, and email spam detection.",
      "Applied machine learning techniques including linear regression and KNN for data analysis and modelling.",
    ],
    stack: ["Python", "Data Analytics"],
  },
  {
    id: "ibm-smartinternz",
    role: "Data Analytics Intern",
    company: "IBM SmartInternz",
    period: "Mar 2023 – Aug 2023",
    points: [
      "Analysed video game sales data and crafted Tableau dashboards to surface key patterns.",
      "Contributed to gaming-trend analysis using HTML, CSS, and JavaScript.",
    ],
    stack: ["Tableau", "Data Analytics", "JavaScript"],
  },
];

export const projects = [
  {
    id: "lead-qualification",
    title: "AI Lead Qualification Automation",
    subTitle: "n8n workflow with LLM-driven enrichment",
    period: "Jan 2026 – Feb 2026",
    icon: "workflow",
    desc: "Built an end-to-end n8n automation that captures leads, enriches them via APIs, qualifies them with LLMs, and routes structured output to Google Sheets and Telegram.",
    features: [
      "Webhook triggers and conditional routing",
      "LLM classification and response generation",
      "API-based lead enrichment",
      "Structured output to Google Sheets and Telegram",
      "Retries, logging and robust error handling",
    ],
    techUsed: ["n8n", "OpenAI API", "Google Sheets API", "Telegram API", "Webhooks"],
    git: "",
  },
  {
    id: "wealth-wiser",
    title: "Wealth Wiser",
    subTitle: "Personal finance GenAI assistant",
    period: "Jul 2024 – Oct 2024",
    icon: "brain",
    desc: "Fine-tuned LLaMA 2-7B with QLoRA on a custom finance dataset, with ChromaDB retrieval behind a Gradio chat interface.",
    features: [
      "LLaMA 2-7B fine-tuned with QLoRA",
      "Custom finance dataset",
      "ChromaDB vector retrieval",
      "Gradio chat interface",
    ],
    techUsed: ["Python", "LLaMA", "Fine-Tuning (QLoRA)", "ChromaDB", "Gradio"],
    git: "",
  },
  {
    id: "weebiller",
    title: "Weebiller",
    subTitle: "Voice-driven POS & automation system",
    period: "Dec 2023 – Aug 2025",
    icon: "mic",
    desc: "A voice-recognition billing system combining speech-to-text, structured data storage and automated bill generation, built for real-world counter use.",
    features: [
      "Voice-recognition billing input",
      "Speech-to-text with ONNX runtime",
      "Python backend with REST-style data flows",
      "Android application",
      "Built for reliability, state management and scale",
    ],
    techUsed: ["Python", "Android Studio", "Speech Recognition", "Java"],
    git: "",
  },
  {
    id: "paecify",
    title: "Paecify",
    subTitle: "Lip-reading model for accessible communication",
    period: "2023",
    icon: "eye",
    desc: "A lip-reading machine learning model that converts lip movements in video into text using a CNN, served through Flask — a communication aid for people with hearing disabilities.",
    features: [
      "CNN-based visual speech recognition",
      "Video-to-text inference pipeline",
      "Flask web interface",
      "Built as an accessibility tool",
    ],
    techUsed: ["Python", "TensorFlow", "Flask", "HTML/CSS"],
    git: "https://github.com/AKILSADIK/IBM-HACK-CHALLENGE",
  },
  {
    id: "questionify",
    title: "Questionify",
    subTitle: "AI question-paper generator",
    period: "Dec 2023 – Jan 2024",
    icon: "file",
    desc: "A question paper generation site that automates building customised, AI-driven papers with a T5-base Hugging Face model. Built in a 9-hour hackathon and placed runner-up at Vervathon'23.",
    features: [
      "T5-base model for question generation",
      "Customisable paper templates",
      "Built in a 9-hour hackathon",
      "Runner-up at Vervathon'23",
    ],
    techUsed: ["Python", "Flask", "HTML/CSS", "JavaScript"],
    git: "",
  },
  {
    id: "speech-dataset",
    title: "Tamil Speech Dataset Collection",
    subTitle: "Crowdsourced audio corpus for STT models",
    period: "2024",
    icon: "wave",
    desc: "A self-initiated effort to collect Tamil audio datasets for training speech-to-text and audio generation models, gathered through a Telegram bot pipeline.",
    features: [
      "Telegram API collection pipeline",
      "Flask backend and storage",
      "Targeted at low-resource Tamil STT",
      "Self-initiated open data effort",
    ],
    techUsed: ["Python", "Flask", "Telegram API"],
    git: "",
  },
];

export const education = {
  degree: "B.Tech – Artificial Intelligence and Data Science",
  institution: "Sona College of Technology",
  location: "Salem, Tamil Nadu",
  period: "Oct 2021 – May 2025",
  score: "CGPA 8.0 / 10",
};

export const certifications = [
  {
    title: "Python for Data Science, AI & Development",
    issuer: "IBM — Coursera",
  },
  {
    title: "Google Data Analytics & Advanced Data Analytics",
    issuer: "Google — Coursera",
  },
  {
    title: "Programming in Java & Introduction to IoT",
    issuer: "NPTEL",
  },
  {
    title: "Advanced Data Analytics & Cyber Security",
    issuer: "Coursera",
  },
];

export const achievements = [
  {
    title: "Olimayam'24",
    result: "Winner",
    prize: "₹1,00,000 grant",
    detail: "",
  },
  {
    title: "KGI's Ideathon'22",
    result: "Winner",
    prize: "₹50,000 cash prize",
    detail: "Represented SONACT with Weebiller as Team Weefy, winning out of 171 teams across Tamil Nadu.",
  },
  {
    title: "Vervathon'23",
    result: "Runner-up",
    prize: "₹2,000 cash prize",
    detail: "Built Questionify, a T5-based question paper generator, in a 9-hour hackathon at SONACT.",
  },
  {
    title: "Best of Best Speaker",
    result: "5th Place",
    prize: "₹1,500 cash prize",
    detail: "",
  },
];

export const leadership = [
  {
    id: "cisco",
    role: "Campus Ambassador",
    org: "Cisco",
    period: "Aug 2023 – Jul 2024",
    point:
      "Promoted Cisco's initiatives and technologies across the Sona College of Technology campus, driving awareness and engagement among students and faculty.",
  },
  {
    id: "build-club",
    role: "President, Build Club",
    org: "IIT Madras Research Park",
    period: "Jul 2023 – Jun 2024",
    point:
      "Led a student build community for software and hardware projects at IITMRP, mentoring members from idea to working prototype.",
  },
];

// Nodes for the animated automation-flow graphic in the hero.
export const heroFlow = [
  { label: "Trigger", tech: "Webhooks" },
  { label: "Automate", tech: "n8n" },
  { label: "Reason", tech: "Claude API" },
  { label: "Ground", tech: "RAG" },
  { label: "Deliver", tech: "REST APIs" },
];
